import './App.css'

import { Button, Col, Layout, Row } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { AppProvider } from './components/common/AppContext'
import AppRoutes from './components/common/Routes'
import { CURRENT_YEAR } from './util/commonUtil'
import ForgotPassword from './components/login/ForgotPassword'
import { Link } from 'react-router-dom'
import Login from './components/login/Login'
import LoginAs from './components/login/LoginAs'
import Menu from './components/common/Menu'
import Messages from './components/common/Messages'
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import ProfileMenu from './components/common/ProfileMenu'
import _ from 'lodash'
import axios from './axios/axios'
import { config } from './util/config'
import { initServerTime } from './util/serverTimeUtil'
import useQueryParams from './hooks/useQueryParams'
import useResponsive from './hooks/useResponsive'

const { Header, Content, Footer } = Layout

const App = () => {
  const { pathname } = useLocation()
  const pageClassName = _.kebabCase(pathname)

  const [messageHidden, setMessageHidden] = useState(false)
  const [loginVisible, setLoginVisible] = useState()
  const [loginAsVisible, setLoginAsVisible] = useState()
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState()
  const [loginInProgress, setLoginInProgress] = useState(true)
  const [currentUser, setCurrentUser] = useState()
  const [menuEnabled, setMenuEnabled] = useState(true)
  const [serverTimeLoaded, setServerTimeLoaded] = useState(false)
  const queryParams = useQueryParams()
  const bp = useResponsive()

  const login = (loginResult) => {
    setCurrentUser(loginResult.user)
    localStorage.setItem('authToken', loginResult.jwt)
    setLoginVisible(false)
    setLoginAsVisible(false)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const header = document.querySelector('.menuRow')
        // If the sentinel is NOT intersecting, the header is stuck
        header.classList.toggle('menuRowStuck', !entry.isIntersecting)
      },
      { threshold: [0] },
    )

    observer.observe(document.querySelector('#sentinel'))

    return () => observer.disconnect()
  }, [])

  const logout = () => {
    setCurrentUser(null)
    localStorage.removeItem('authToken')
  }

  const VALIDATE_TIMEZONE = true
  const timezoneOffset = new Date().getTimezoneOffset() / 60
  const validTimezone = !VALIDATE_TIMEZONE ? true : timezoneOffset === 4 || timezoneOffset === 5

  const handleForgotPassword = () => {
    setLoginVisible(false)
    setForgotPasswordVisible(true)
  }

  // const appContext = {
  //   currentUser,
  //   setCurrentUser,
  //   loginInProgress,
  //   login,
  //   logout,
  //   menuEnabled,
  //   setLoginVisible,
  //   handleForgotPassword,
  //   setMessageHidden,
  //   messageHidden,
  //   validTimezone,
  //   serverTimeLoaded,
  // }

  const appContext = useMemo(
    () => ({
      currentUser,
      setCurrentUser,
      loginInProgress,
      login,
      logout,
      menuEnabled,
      setLoginVisible,
      setLoginAsVisible,
      handleForgotPassword,
      setMessageHidden,
      messageHidden, // React will look at this dependency
      validTimezone,
      serverTimeLoaded,
    }),
    [currentUser, loginInProgress, menuEnabled, messageHidden, validTimezone, serverTimeLoaded],
  )

  useEffect(() => {
    ;(async () => {
      await initServerTime()
      setServerTimeLoaded(true)
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!currentUser && loginInProgress) {
        try {
          setLoginInProgress(true)
          const result = await axios.get('/users/me')
          const curUser = result?.data
          setCurrentUser(curUser)
        } catch (e) {
          console.error(e)
        } finally {
          setLoginInProgress(false)
        }
      }
    })()
  }, [currentUser, loginInProgress])

  const initialOptions = {
    currency: 'CAD',
    intent: 'capture',
    'client-id': config.PAYPAL_CLIENTID,
  }

  const wrapHeader = bp.xsOrSm

  return (
    <PayPalScriptProvider options={initialOptions}>
      <AppProvider value={appContext}>
        <div className={`background ${pageClassName}`} />
        <div className={`page ${pageClassName}`}>
          <div id="sentinel"></div>
          {menuEnabled && (
            <Row className="menuRow" justify="space-between" wrap={false}>
              <Menu />
              <ProfileMenu onShowLogin={() => setLoginVisible(true)} />
            </Row>
          )}
          <Layout className="layout">
            <Header className="header" style={{ zIndex: 4 }}>
              <Row wrap={wrapHeader} justify="space-between">
                <Col>
                  <Link to="/">
                    <img className="logo" alt="" />
                  </Link>
                </Col>
              </Row>
            </Header>
            <Col
              xs={20}
              sm={20}
              md={20}
              lg={20}
              xl={14}
              xxl={12}
              style={{
                position: 'absolute',
                float: 'right',
                right: '0',
                paddingRight: '10px',
                paddingTop: '10px',
              }}
            >
              <Messages />
            </Col>
            <Content className="content">
              <AppRoutes />
            </Content>
            <Footer className="footer">
              © {CURRENT_YEAR} Hespeler Tennis Club &middot; 23 Forbes St Cambridge, ON
            </Footer>
          </Layout>
        </div>
        <Login
          visible={loginVisible}
          onSuccess={login}
          onClose={() => setLoginVisible(false)}
          onForgotPassword={handleForgotPassword}
        />
        <LoginAs visible={loginAsVisible} onSuccess={login} onClose={() => setLoginAsVisible(false)} />
        <ForgotPassword
          visible={forgotPasswordVisible}
          onSuccess={() => setForgotPasswordVisible(false)}
          onClose={() => setForgotPasswordVisible(false)}
        />
      </AppProvider>
    </PayPalScriptProvider>
  )
}

export default App
